// src/components/ProtectedRoute.tsx
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  // Extract primary role from roles array or use role property for backward compatibility
  const primaryRole = user?.role || (Array.isArray(user?.roles) && user.roles[0]) || undefined;
  
  console.log('🔒 ProtectedRoute: Check - loading:', loading, 'user:', user?.email, 'roles:', user?.roles, 'primaryRole:', primaryRole, 'allowed:', allowedRoles);

  if (loading) {
    console.log('🔒 ProtectedRoute: Still loading, showing loading state');
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    console.log('🔒 ProtectedRoute: No user, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  // If allowedRoles is specified, check if user's roles array includes an allowed role
  if (allowedRoles) {
    const userRoles = user.roles || (user.role ? [user.role] : []);
    const hasAllowedRole = Array.isArray(userRoles) && userRoles.some(role => allowedRoles.includes(role));
    
    if (!hasAllowedRole) {
      // User's role is not allowed for this route, redirect them
      console.log('🔒 ProtectedRoute: User roles', userRoles, 'not in allowed roles', allowedRoles, 'redirecting');
      if (userRoles.includes('admin')) {
        return <Navigate to="/admin/dashboard" replace />;
      } else if (userRoles.includes('provider')) {
        return <Navigate to="/provider/dashboard" replace />;
      } else {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  console.log('🔒 ProtectedRoute: Access granted for', user.email);
  return <>{children}</>;
};

export default ProtectedRoute;